/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

// export const formatCurrency = (amount: number | string | null) => { ... }
// export const formatDate = (dateStr: string) => { ... }  // Helper function to format currency
  export const formatCurrency = (amount: number | string | null | undefined) => {
    const numAmount = parseFloat(String(amount || 0));
    const safeAmount = isNaN(numAmount) ? 0 : numAmount;
    return `$${safeAmount.toFixed(2)}`;
  };

  // Simple function to format dates
  export const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    const time = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });
    return `${month}/${day}/${year} at ${time}`;
  };

// Robustly parses a date into a Date object, sidestepping cross-engine
// string-parsing differences for non-ISO formats. Used for any actual
// date math/comparison (not just display) - e.g. AdminPayouts.tsx and
// UserPayouts.tsx finding a collection's most recent payout date, which
// used to call `new Date(payout.date)` directly. That silently broke on
// iOS Safari/WKWebView: an Invalid Date compares as neither greater nor
// less than anything, so the "most recent" reduce never picked up a
// payout whose date failed to parse there, silently falling back to the
// reduce's epoch starting value instead - collapsing the "since last
// payout" cutoff to 1970 and scanning full order history, while Chrome's
// lenient parser got the same string right and used the real cutoff.
export const parseDateRobust = (dateStr: string | Date | null | undefined): Date => {
  if (!dateStr) {
    return new Date(NaN);
  }

  // Handle Firestore Timestamp objects
  if (typeof dateStr === 'object' && 'toDate' in dateStr) {
    return (dateStr as any).toDate();
  }

  // Handle Date objects
  if (dateStr instanceof Date) {
    return dateStr;
  }

  if (typeof dateStr === 'string') {
    // Check if it's an ISO string (has T or Z) - don't modify these
    if (dateStr.includes('T') || dateStr.includes('Z')) {
      return new Date(dateStr);
    }
    // iOS Safari fix: a leading YYYY-MM-DD with no T/Z - whether that's
    // the whole string or it's followed by a space-separated time (e.g.
    // "2026-01-15 00:00:00") - breaks WebKit's Date parser even though
    // Chrome parses it leniently. Building the Date from plain numbers
    // sidesteps string-parsing differences between engines entirely.
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const [, year, month, day] = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/)!;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    // Legacy MM-DD-YYYY format (e.g. "01-02-2026") - predates the
    // <input type="date"> form, which always produces YYYY-MM-DD.
    // Chrome's lenient parser guesses this as US-style and gets it
    // right; WebKit's stricter parser doesn't recognize it at all and
    // returns Invalid Date. Same plain-number fix as above.
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [, month, day, year] = dateStr.match(/^(\d{2})-(\d{2})-(\d{4})$/)!;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    // Handle other string formats normally
    return new Date(dateStr);
  }

  return new Date(dateStr as any);
};

 // Format date without time (just the date)
export const formatDateOnly = (dateStr: string | Date | null | undefined) => {
  if (!dateStr) {
    return 'N/A';
  }

  try {
    const date = parseDateRobust(dateStr);

    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateStr);
      return 'Invalid Date';
    }

    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    };

    return date.toLocaleDateString('en-US', options);
  } catch (error) {
    console.error('Error formatting date:', error, dateStr);
    return 'Invalid Date';
  }
};